import { CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { Header } from '@vms/Header';
import { IconPlus } from '@svg';
import { Table } from '@components/Table';
import { Route, RouteParams } from '@core/routes/Route';
import Button, { ButtonType } from '@components/Button';
import { copyToClipboard, formatDefaultDate, replaceRouteParam } from '@core/utils/string';
import useCommonEnums, { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';
import { useCountryName } from '@core/hooks/useCountryOptions';
import SurveyItemsFilters from '@components/SurveyItemsFilters';
import { TextInput } from '@components/Inputs';

import useSurveyDetails from '@pages/SurveyDetails/useSurveyDetails';
import CustomFielding from '@pages/SurveyDetails/components/CustomFielding';
import PerRespondent from '@pages/SurveyDetails/components/PerRespondent';
import SurveyDetailsTableConfig from '@pages/SurveyDetails/SurveyDetailsTableConfig';
import KeyMetricAverage from '@pages/SurveyDetails/components/KeyMetricAverage';
import {
    useGetSurveyDefaultDemographic,
    useGetSurveyDefaultAgeGroups,
    useGetSurveyInitialData,
    SurveyDetailsItem,
} from '@core/api/useSurveys';
import ItemsRatio from '@pages/SurveyDetails/components/ItemsRatio';
import ItemsCount from '@pages/SurveyDetails/components/ItemsCount';
import DemographicManagement from '@pages/SurveyDetails/components/DemographicManagement';
import ProviderManagement from '@pages/SurveyDetails/components/ProviderManagement';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Toggle } from '@components/Toggle';
import cn from 'classnames';
import { toast } from 'react-toastify';
import RemoveRespondentsPopup from '@pages/SurveyDetails/components/RemoveRespondentsPopup';

const SurveyDetails = () => {
    const [isDemographicOpened, setIsDemographicOpened] = useState(false);
    const [isProviderOpened, setIsProviderOpened] = useState(false);
    const [isCustomFieldingOpened, setIsCustomFieldingOpened] = useState(true);
    const { findCommonNameById } = useCommonEnums();
    const [itemsToCloseOrPublish, setItemsToCloseOrPublish] = useState<number[]>([]);
    const [isRemoveRespondentOpened, setIsRemoveRespondentOpened] = useState(false);
    const [filteredSurveyItems, setFilteredSurveyItems] = useState<SurveyDetailsItem[]>([]);

    const {
        data: initialData,
        isLoading: isInitialDataLoading,
    } = useGetSurveyInitialData();

    const {
        data: {
            surveyDetails,
            activeItems,
            isLoading: isSurveyLoadingOrSaving,
        },
        form,
        onDeleteItems,
        onSaveSurvey,
        getSurveyDetails,
    } = useSurveyDetails();

    // Set filtered items when survey items change
    useEffect(() => {
        setFilteredSurveyItems(activeItems);
    }, [activeItems]);

    const {
        data: initialDemographicGroups,
        isLoading: isDefaultDemographicGroupsLoading,
        doFetch: fetchDefaultDemographicGroups,
    } = useGetSurveyDefaultDemographic();

    const {
        data: initialAgeGroups,
        isLoading: isDefaultAgeGroupsLoading,
        doFetch: fetchDefaultAgeGroups,
    } = useGetSurveyDefaultAgeGroups();

    useEffect(() => {
        if (surveyDetails?.countryId) {
            const params = {
                countryId: surveyDetails.countryId,
            };
            fetchDefaultDemographicGroups({
                params,
            }).catch(console.error);
            fetchDefaultAgeGroups({
                params,
            }).catch(console.error);
        }
    }, [surveyDetails?.countryId, fetchDefaultDemographicGroups, fetchDefaultAgeGroups]);

    const saveSurveyWithItemsToCloseOrPublish = useCallback(() => {
        onSaveSurvey(itemsToCloseOrPublish);
    }, [itemsToCloseOrPublish, onSaveSurvey]);

    const countryName = useCountryName(surveyDetails?.countryId ?? 0);

    const isLoading = isSurveyLoadingOrSaving || isInitialDataLoading || isDefaultDemographicGroupsLoading || isDefaultAgeGroupsLoading;

    const defaultAgeGroups = useMemo(() => initialAgeGroups ?? [], [initialAgeGroups]);
    const defaultDemographicGroups = useMemo(() => initialDemographicGroups ?? [], [initialDemographicGroups]);

    const isSurveyClosed = useMemo(() => {
        return surveyDetails?.status === SurveyStatusIdEnum.Closed;
    }, [surveyDetails?.status]);

    if (isLoading) {
        return (
            <div className="SurveyDetails__loader">
                <CircularProgress size={60}/>
            </div>
        );
    }

    return (
        <div className="SurveyDetails">
            <div className="SurveyDetails__header">
                <div className="SurveyDetails__header-col">
                    <div className="SurveyDetails__name">Survey Name: {surveyDetails?.customName}</div>
                </div>
                <div className="SurveyDetails__header-col">
                    {
                        isSurveyClosed ? (
                            <Button
                                modifiers={['h-tall', 'no-transform']}
                                onClick={() => {
                                    setIsRemoveRespondentOpened(true);
                                }}
                            >
                                Remove Respondents / Restart Data Generation
                            </Button>
                        ) : (
                            surveyDetails?.links?.map((link) => (
                                <Button
                                    key={link.providerName}
                                    modifiers={['h-tall', 'no-transform']}
                                    onClick={() => {
                                        copyToClipboard(link.url ?? '');
                                        toast.success(`${link.providerName} Survey Link copied!`);
                                    }}
                                >
                                    <ContentCopyIcon/> {link.providerName} Link
                                </Button>
                            ))
                        )
                    }

                    <TextInput
                        value={form.state.cmixId ?? ''}
                        setValue={form.actions.setField.bind(null, 'cmixId')}
                        placeholder="CMIX Survey ID"
                        isDisabled={surveyDetails?.status !== SurveyStatusIdEnum.Draft}
                        style={{ textAlign: 'center' }}
                    />
                </div>
            </div>
            <Header
                style={{
                    marginBottom: isCustomFieldingOpened ? undefined : 0,
                }}
            >
                <div
                    className={cn(
                        'SurveyDetails__secondary-title',
                        isCustomFieldingOpened && 'is-open'
                    )}
                    onClick={() => setIsCustomFieldingOpened(!isCustomFieldingOpened)}
                >
                    <span className="SurveyDetails__secondary-title-icon"/>
                    Fielding Parameters
                </div>
            </Header>
            {isCustomFieldingOpened && (
                <div className="SurveyDetails__fielding-container">
                    <div className="SurveyDetails__param-column">
                        <div className="SurveyDetails__section-title">Summary</div>
                        <div className="SurveyDetails__param-row">
                            <div className="SurveyDetails__param-title">Survey Status:</div>
                            <div className="SurveyDetails__param-value">
                                {findCommonNameById('surveyStatuses', surveyDetails?.status)}
                            </div>
                        </div>
                        <div className="SurveyDetails__param-row">
                            <div className="SurveyDetails__param-title">Fielding start date:</div>
                            <div className="SurveyDetails__param-value">
                                {surveyDetails?.startDate ? formatDefaultDate(surveyDetails?.startDate) : '- - -'}
                            </div>
                        </div>
                        <div className="SurveyDetails__param-row">
                            <div className="SurveyDetails__param-title">Fielding end date:</div>
                            <div className="SurveyDetails__param-value">
                                {surveyDetails?.endDate ? formatDefaultDate(surveyDetails?.endDate) : '- - -'}
                            </div>
                        </div>
                        <div className="SurveyDetails__param-row">
                            <div className="SurveyDetails__param-title">Country:</div>
                            <div className="SurveyDetails__param-value">
                                {countryName || '- - -'}
                            </div>
                        </div>
                        <div className="SurveyDetails__param-row">
                            <div className="SurveyDetails__param-title" style={{ marginTop: '2px' }}>custom fielding:
                            </div>
                            <CustomFielding
                                form={form}
                                surveyStatusId={surveyDetails?.status}
                                customFieldingOptions={initialData?.customFielding ?? []}
                            />
                        </div>
                        {surveyDetails?.status !== SurveyStatusIdEnum.Closed && (
                            <>
                                <div className="SurveyDetails__param-row">
                                    <Toggle
                                        label="Enable gender/ethnicity quotas"
                                        isChecked={!!form.state.demographicGroupLogic}
                                        onChange={() => form.actions.setField('demographicGroupLogic', !form.state.demographicGroupLogic)}
                                    />
                                </div>
                                <div className="SurveyDetails__param-row">
                                    <Toggle
                                        label="Enable generation quotas"
                                        isChecked={!!form.state.ageLogic}
                                        onChange={() => form.actions.setField('ageLogic', !form.state.ageLogic)}
                                    />
                                </div>
                                <div className="SurveyDetails__param-row">
                                    <Toggle
                                        label="Enable concepts priority"
                                        isChecked={!!form.state.conceptsPriority}
                                        onChange={() => form.actions.setField('conceptsPriority', !form.state.conceptsPriority)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <ItemsCount data={surveyDetails?.itemsCount}/>
                    {surveyDetails?.status === SurveyStatusIdEnum.Closed && (
                        <KeyMetricAverage keyMetricAverages={surveyDetails?.keyMetricAverages}/>
                    )}

                    {surveyDetails?.status !== SurveyStatusIdEnum.Closed && (
                        <>
                            <PerRespondent totalSurveySample={surveyDetails?.totalSurveySample} form={form}/>
                            <ItemsRatio form={form}/>
                        </>
                    )}
                </div>
            )}
            {initialData !== null && surveyDetails?.status !== SurveyStatusIdEnum.Closed && (
                <DemographicManagement
                    form={form}
                    initialData={initialData}
                    defaultAgeGroups={defaultAgeGroups}
                    defaultDemographicGroups={defaultDemographicGroups}
                    isOpened={isDemographicOpened}
                    setIsOpened={setIsDemographicOpened}
                />
            )}
            {initialData !== null && surveyDetails?.status !== SurveyStatusIdEnum.Closed && (
                <ProviderManagement
                    form={form}
                    initialData={initialData}
                    isOpened={isProviderOpened}
                    setIsOpened={setIsProviderOpened}
                />
            )}
            <div className="Separator"/>
            <Header
                rightContent={
                    <div className="ChainItems__actions">
                        {surveyDetails?.status === SurveyStatusIdEnum.Draft && (
                            <Button
                                type={ButtonType.Link}
                                modifiers={['h-tall']}
                                link={replaceRouteParam(Route.SurveyAddItems, RouteParams.Id, surveyDetails?.id ?? '')}
                            >
                                <IconPlus/>
                                Add New Item
                            </Button>
                        )}
                    </div>
                }
            >
                <span className="SurveyDetails__secondary-title">included items</span>
                <SurveyItemsFilters
                    className='SurveyDetails__filters SurveyDetailsFilters'
                    items={activeItems}
                    countryId={surveyDetails?.countryId ?? 0}
                    onFilteredItemsChange={setFilteredSurveyItems}
                />
            </Header>
            <div className="SurveyDetails__table-container">
                <SurveyDetailsTableConfig
                    data={filteredSurveyItems}
                    surveyStatus={surveyDetails?.status}
                    onDeleteItems={onDeleteItems}
                    onSelectedItemsChange={setItemsToCloseOrPublish}
                >
                    <Table rowsCount={filteredSurveyItems.length} isLoading={false}/>
                </SurveyDetailsTableConfig>
            </div>

            <div className="SurveyDetails__footer">
                <Button modifiers={['h-tall']} onClick={saveSurveyWithItemsToCloseOrPublish}>
                    Save & Exit
                </Button>
            </div>

            {
                isSurveyClosed ? (
                    <RemoveRespondentsPopup
                        open={isRemoveRespondentOpened}
                        surveyName={surveyDetails?.customName ?? ""}
                        surveyId={surveyDetails?.id ?? 0}
                        handleClosePopup={() => setIsRemoveRespondentOpened(false)}
                        onOperationComplete={getSurveyDetails}
                    />
                ) : null
            }
        </div>
    );
};

export default SurveyDetails;
